import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faFileInvoice, faArrowRight, faChevronDown, faChevronUp, faBox } from '@fortawesome/free-solid-svg-icons';
import './InvoiceAdjustmentModal.css';
import { PRICE_DECIMALS } from '../../utils/precision';

const InvoiceAdjustmentModal = ({ 
  isOpen, 
  onClose, 
  adjustmentSummary, 
  currency,
  onNavigateToCreditNote,
  onNavigateToDebitNote 
}) => {
  // Track which credit/debit notes are expanded to show line items
  const [expandedNotes, setExpandedNotes] = useState({});
  
  if (!isOpen || !adjustmentSummary) return null;
  
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  // A unit price carries up to PRICE_DECIMALS: show every decimal it
  // genuinely has (at least 2) instead of rounding it to cents.
  const formatPrice = (amount) => {
    if (amount === null || amount === undefined) return '0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: PRICE_DECIMALS
    }).format(amount);
  };
  
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const toggleExpand = (noteId, e) => {
    e.stopPropagation(); // Prevent navigation when expanding
    setExpandedNotes(prev => ({
      ...prev,
      [noteId]: !prev[noteId]
    }));
  };
  
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  return (
    <div className="adjustment-modal-overlay" onClick={handleOverlayClick}>
      <div className="adjustment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="adjustment-modal-header">
          <h3><FontAwesomeIcon icon={faFileInvoice} /> Invoice Adjustments</h3>
          <button className="adjustment-close-btn" onClick={onClose} type="button">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        
        <div className="adjustment-modal-body">
          {/* Credit Notes */}
          {adjustmentSummary.credit_notes?.length > 0 && (
            <div className="adjustment-section">
              <h4 className="adjustment-section-title credit">
                Credit Notes ({adjustmentSummary.credit_notes_count})
              </h4>
              {adjustmentSummary.credit_notes.map(cn => (
                <div key={cn.id} className="adjustment-note-card">
                  {/* Header - Clickable to navigate */}
                  <div 
                    className="adjustment-item" 
                    onClick={() => onNavigateToCreditNote(cn.id)}
                  >
                    <div className="adjustment-item-content">
                      <span className="adjustment-item-number">{cn.credit_note_number}</span>
                      <span className="adjustment-item-date">{formatDate(cn.invoice_date)}</span>
                    </div>
                    <div className="adjustment-item-actions">
                      <span className="adjustment-item-amount credit">
                        -{formatCurrency(cn.gross_amount)}
                      </span>
                      <button 
                        className="expand-toggle-btn"
                        onClick={(e) => toggleExpand(`credit-${cn.id}`, e)}
                        title={expandedNotes[`credit-${cn.id}`] ? 'Hide products' : 'Show products'}
                        type="button"
                      >
                        <FontAwesomeIcon icon={expandedNotes[`credit-${cn.id}`] ? faChevronUp : faChevronDown} />
                      </button>
                      <FontAwesomeIcon icon={faArrowRight} className="navigate-icon" />
                    </div>
                  </div>
                  
                  {/* Expandable Line Items */}
                  {expandedNotes[`credit-${cn.id}`] && cn.line_items?.length > 0 && (
                    <div className="line-items-detail">
                      <div className="line-items-header">
                        <FontAwesomeIcon icon={faBox} /> Products Credited:
                      </div>
                      {cn.line_items.map((item, idx) => (
                        <div key={idx} className="line-item-row">
                          <div className="line-item-desc">{item.item_description}</div>
                          <div className="line-item-qty">
                            {item.quantity} {item.unit} × {formatPrice(item.unit_price)}
                          </div>
                          <div className="line-item-total credit">
                            -{formatCurrency(item.gross_amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="adjustment-subtotal">
                <span>Total Credits:</span>
                <span className="credit">-{formatCurrency(adjustmentSummary.total_credits)}</span>
              </div>
            </div>
          )}
          
          {/* Debit Notes */}
          {adjustmentSummary.debit_notes?.length > 0 && (
            <div className="adjustment-section">
              <h4 className="adjustment-section-title debit">
                Debit Notes ({adjustmentSummary.debit_notes_count})
              </h4>
              {adjustmentSummary.debit_notes.map(dn => (
                <div key={dn.id} className="adjustment-note-card">
                  {/* Header - Clickable to navigate */}
                  <div 
                    className="adjustment-item" 
                    onClick={() => onNavigateToDebitNote(dn.id)}
                  >
                    <div className="adjustment-item-content">
                      <span className="adjustment-item-number">{dn.debit_note_number}</span>
                      <span className="adjustment-item-date">{formatDate(dn.invoice_date)}</span>
                    </div>
                    <div className="adjustment-item-actions">
                      <span className="adjustment-item-amount debit">
                        +{formatCurrency(dn.gross_amount)}
                      </span>
                      <button 
                        className="expand-toggle-btn"
                        onClick={(e) => toggleExpand(`debit-${dn.id}`, e)}
                        title={expandedNotes[`debit-${dn.id}`] ? 'Hide products' : 'Show products'}
                        type="button"
                      >
                        <FontAwesomeIcon icon={expandedNotes[`debit-${dn.id}`] ? faChevronUp : faChevronDown} />
                      </button>
                      <FontAwesomeIcon icon={faArrowRight} className="navigate-icon" />
                    </div>
                  </div>
                  
                  {/* Expandable Line Items */}
                  {expandedNotes[`debit-${dn.id}`] && dn.line_items?.length > 0 && (
                    <div className="line-items-detail">
                      <div className="line-items-header">
                        <FontAwesomeIcon icon={faBox} /> Products Debited:
                      </div>
                      {dn.line_items.map((item, idx) => (
                        <div key={idx} className="line-item-row">
                          <div className="line-item-desc">{item.item_description}</div>
                          <div className="line-item-qty">
                            {item.quantity} {item.unit} × {formatPrice(item.unit_price)}
                          </div>
                          <div className="line-item-total debit">
                            +{formatCurrency(item.gross_amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <div className="adjustment-subtotal">
                <span>Total Debits:</span>
                <span className="debit">+{formatCurrency(adjustmentSummary.total_debits)}</span>
              </div>
            </div>
          )}
          
          {/* Summary */}
          <div className="adjustment-summary">
            <div className="adjustment-summary-line">
              <span>Original Invoice Amount:</span>
              <span>{formatCurrency(adjustmentSummary.original_amount)}</span>
            </div>
            <div className="adjustment-summary-line net">
              <span><strong>Net Amount Due:</strong></span>
              <span><strong>{formatCurrency(adjustmentSummary.net_amount_due)}</strong></span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoiceAdjustmentModal;
